import {
  Button,
  HStack,
  Text,
  Textarea,
  useToast,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Flex,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useAgreementPlans } from '@partiu/api/dashboard/hooks.ts';
import { IPlan } from '@partiu/api/dashboard/types.ts';
import { Labeled } from '@partiu/ui/components/molecules/Labeled.tsx';
import { FaFile } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { AddModalProps } from '../../types';
import { loadCSV } from '@partiu/ui/utils.ts';
import validateCNPJ from '../../utils/CNPJValidator.ts';
import validateCPF from '../../utils/CPFValidator.ts';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { useModules } from '../../contexts/ProductsProvider.tsx';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { createBulkSubscriptions } from '@partiu/api/dashboard/api.ts';

function sanitizeCPFandCNPJText(text: string): string[] {
  const cpfAndCnpjArray = text.split('\n');
  const filteredCPFCNPJArray = cpfAndCnpjArray.filter((entry) => {
    const numericValue = entry.replace(/\D/g, '');
    return numericValue.trim() !== '' && (validateCPF(numericValue) || validateCNPJ(numericValue));
  });
  const sanitizedCPFCNPJArray = filteredCPFCNPJArray.map((entry) => {
    const numericValue = entry.replace(/\D/g, '');
    if (numericValue.length === 11) {
      return numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return numericValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  });
  return [...new Set(sanitizedCPFCNPJArray)];
}

interface SelectedPlan {
  id: string | number;
  name: string;
  agreementName: string;
  displayName: string;
}

interface PlanOption {
  value: string | number;
  label: string;
  plan: IPlan;
}

export function SubscriptionForm({ onClose = () => {} }: AddModalProps<unknown>) {
  const toast = useToast();
  const [selectedPlans, setSelectedPlans] = useState<SelectedPlan[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0); // Novo estado para progresso
  const [totalBatches, setTotalBatches] = useState(0); // Novo estado para progresso
  const [batchError, setBatchError] = useState<string | null>(null); // Erro por lote

  // cpfModule vem do produto Super App. Já externalCodeSubscriptionModule não tem
  // chave equivalente no metadata do produto, então continua vindo do agreement.
  const { cpfModule } = useModules();
  const { extraData } = useTheme();
  const externalCodeSubscriptionModule = extraData?.externalCodeSubscriptionModule;

  const [cpfs, setCPFS] = useState('');
  const [sanitizedCPFS, setSanitizedCPFS] = useState<string[]>([]);
  const [sanitizedCode, setSanitizedCode] = useState<string[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [code, setCode] = useState('');

  const { data } = useAgreementPlans();
  const plans: IPlan[] = Array.isArray(data?.results) ? data?.results : [];

  console.log(plans);
  // Converte planos para opções do react-select
  const planOptions: PlanOption[] = useMemo(() => {
    return (plans || []).map((plan: IPlan) => ({
      value: plan.id,
      label: `${plan.display_name}`,
      plan: plan,
    }));
  }, [plans]);

  // Valores selecionados para o react-select
  const selectedPlanOptions = useMemo(() => {
    return selectedPlans
      .map((plan) => {
        const foundPlan = planOptions.find((option) => option.value === plan.id)?.plan;
        console.log(foundPlan);
        console.log(plan);
        if (!foundPlan) return null;
        return {
          value: plan.id,
          label: `${plan.display_name}`,
          plan: foundPlan,
        };
      })
      .filter((option): option is PlanOption => option !== null);
  }, [selectedPlans, planOptions]);

  const setCPFandCNPJData = (text: string) => {
    setCPFS(text);
    const sanitizedCPFsList = sanitizeCPFandCNPJText(text);
    setSanitizedCPFS(sanitizedCPFsList);
  };

  const setCodeData = (text: string) => {
    setCode(text);
    const sanitizedCodeList = text.split('\n').filter((entry) => entry.trim() !== '');
    setSanitizedCode(sanitizedCodeList);
  };

  const handlePlanSelectionChange = (selectedOptions: MultiValue<PlanOption>) => {
    const newSelectedPlans = selectedOptions.map((option: PlanOption) => ({
      id: option.plan.id,
      name: option.plan.name,
      agreementName: option.plan.agreement.name,
      display_name: option.plan.display_name,
    }));
    setSelectedPlans(newSelectedPlans);
  };

  // Função utilitária para dividir em lotes
  function chunkArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }

  const processSubscriptions = useCallback(async () => {
    if (selectedPlans.length === 0) {
      toast({
        title: 'Erro',
        description: 'Selecione pelo menos um plano',
        status: 'error',
      });
      return;
    }

    const dataToProcess = tabIndex === 0 ? sanitizedCPFS : sanitizedCode;
    if (dataToProcess.length === 0) {
      toast({
        title: 'Erro',
        description: 'Insira dados válidos para processar',
        status: 'error',
      });
      return;
    }

    setIsProcessing(true);
    setBatchError(null);

    // Quebra em lotes de 500
    const batches = chunkArray(dataToProcess, 500);
    setTotalBatches(batches.length);
    setCurrentBatch(0);

    let totalProcessed = 0;
    let successBatches = 0;
    let errorBatches = 0;

    toast({
      title: `Iniciando processamento de ${batches.length} lote(s)...`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });

    for (let i = 0; i < batches.length; i++) {
      setCurrentBatch(i + 1);
      setBatchError(null);
      toast({
        title: `Processando lote ${i + 1} de ${batches.length}...`,
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      let subscriptionData: { cpfs?: { cpf: string; plans_id: (string | number)[] }[]; external_codes?: { code: string; plans_id: (string | number)[] }[] };
      const planIds = selectedPlans.map(plan => plan.id);
      if (tabIndex === 0) {
        subscriptionData = {
          cpfs: batches[i].map(cpf => ({
            cpf: cpf,
            plans_id: planIds
          }))
        };
      } else {
        subscriptionData = {
          external_codes: batches[i].map(code => ({
            code: code,
            plans_id: planIds
          }))
        };
      }
      try {
        await createBulkSubscriptions(subscriptionData);
        totalProcessed += batches[i].length;
        successBatches++;
        toast({
          title: `Lote ${i + 1} processado com sucesso!`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        errorBatches++;
        setBatchError('Erro ao processar o lote ' + (i + 1));
        toast({
          title: `Erro ao processar lote ${i + 1}`,
          description: error instanceof Error ? error.message : undefined,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    }

    setIsProcessing(false);
    setCurrentBatch(0);
    setTotalBatches(0);

    if (errorBatches === 0) {
      toast({
        title: 'Processamento Concluído',
        description: `${totalProcessed} itens processados com sucesso (${successBatches} lote(s))`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      setTimeout(() => {
        onClose();
      }, 1000);
    } else {
      toast({
        title: 'Processamento finalizado com erros',
        description: `${successBatches} lote(s) com sucesso, ${errorBatches} lote(s) com erro. Total processado: ${totalProcessed}`,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
    }
  }, [selectedPlans, tabIndex, sanitizedCPFS, sanitizedCode, toast, onClose]);

  const tabMessages = ['Insira um CPF/CNPJ por linha', 'Insira o código do usuário'];

  useEffect(() => {
    if (cpfModule && externalCodeSubscriptionModule) {
      setTabIndex(0);
    } else if (cpfModule) {
      setTabIndex(0);
    } else if (externalCodeSubscriptionModule) {
      setTabIndex(1);
    }
  }, [cpfModule, externalCodeSubscriptionModule]);

  const hasValidData = (tabIndex === 0 && sanitizedCPFS.length > 0) || (tabIndex === 1 && sanitizedCode.length > 0);
  const canProcess = hasValidData && selectedPlans.length > 0 && !isProcessing;

  // Estilos customizados para o react-select
  const selectStyles: StylesConfig<PlanOption, true> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      fontSize: '14px',
      borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
      '&:hover': {
        borderColor: '#cbd5e0',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#ebf8ff',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#2b6cb0',
      fontSize: '13px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#2b6cb0',
      ':hover': {
        backgroundColor: '#bee3f8',
        color: '#1e4a72',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#a0aec0',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '14px',
      backgroundColor: state.isSelected ? '#3182ce' : state.isFocused ? '#ebf8ff' : 'white',
      color: state.isSelected ? 'white' : '#2d3748',
      ':active': {
        backgroundColor: '#2c5aa0',
      },
    }),
  };

  return (
    <VStack spacing={4} align={'flex-start'} width="100%">
      <Tabs index={tabIndex} onChange={setTabIndex} width="100%" size="sm">
        <TabList>
          <Tab hidden={!cpfModule} fontSize="sm">
            CPF/CNPJ
          </Tab>
          <Tab hidden={!externalCodeSubscriptionModule} fontSize="sm">
            Código
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel hidden={!cpfModule} p={0} pt="2">
            <Text fontSize={'9pt'} color={'#a9a9a9'} mb={1}>
              {tabMessages[0]}
            </Text>
            <Textarea
              value={cpfs}
              onChange={(e) => setCPFandCNPJData(e.target.value)}
              minHeight={'120px'}
              placeholder={'Digite aqui os CPFs / CNPJs'}
              size="sm"
            />
            <Text fontSize={11} color={'#a5a5a5'} mt={1}>
              {sanitizedCPFS.length} CPFs/CNPJs válidos e distintos encontrados.
            </Text>
          </TabPanel>
          <TabPanel hidden={!externalCodeSubscriptionModule} p={0} pt="2">
            <Text fontSize={'9pt'} color={'#a9a9a9'} mb={1}>
              {tabMessages[1]}
            </Text>
            <Textarea
              value={code}
              onChange={(e) => setCodeData(e.target.value)}
              minHeight={'120px'}
              placeholder={'Digite aqui o código do usuário'}
              size="sm"
            />
            <Text fontSize={11} color={'#a5a5a5'} mt={1}>
              {sanitizedCode.length} códigos válidos e distintos encontrados.
            </Text>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Labeled label={'Planos'} labelSize="14px">
        <Box width="100%">
          <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
            Selecione os planos ({selectedPlans.length} selecionado(s))
          </Text>
          <Select<PlanOption, true>
            isMulti
            options={planOptions}
            value={selectedPlanOptions}
            onChange={handlePlanSelectionChange}
            placeholder="Selecione os planos..."
            noOptionsMessage={() => 'Nenhum plano encontrado'}
            isDisabled={isProcessing}
            styles={selectStyles}
            closeMenuOnSelect={false}
            isSearchable
            menuPlacement="auto"
            maxMenuHeight={200}
          />
          {selectedPlans.length > 0 && (
            <Text fontSize="xs" color="blue.600" mt={1}>
              ✓ {selectedPlans.length} plano(s) selecionado(s) para processamento
            </Text>
          )}
        </Box>
      </Labeled>

      {isProcessing && (
        <Alert status={batchError ? "error" : "info"} borderRadius="md" size="sm">
          <AlertIcon />
          <Text fontWeight="medium" fontSize="sm">
            {batchError
              ? batchError
              : `Processando lote ${currentBatch} de ${totalBatches}...`}
          </Text>
        </Alert>
      )}

      <Box borderTop="1px solid" borderColor="gray.200" pt={4} w={'100%'} mt={4}>
        <Alert status="warning" borderRadius="md" size="sm" mb={3}>
          <AlertIcon />
          <Text fontSize="sm">
            O processamento será feito em lotes de até 500 itens por vez e pode demorar dependendo da quantidade enviada.
          </Text>
        </Alert>
        <Flex align="center" justify="space-between">
          <Button
            leftIcon={<FaFile />}
            variant="outline"
            colorScheme="blue"
            size="sm"
            onClick={() =>
              loadCSV((csvText: string) => {
                if (tabIndex === 0) {
                  setCPFandCNPJData(csvText);
                  toast({
                    title: 'CSV Carregado.',
                    description: `${sanitizeCPFandCNPJText(csvText).length} CPFs/CNPJs carregados com sucesso.`,
                  });
                } else {
                  setCodeData(csvText);
                  toast({
                    title: 'CSV Carregado.',
                    description: `${
                      csvText.split('\n').filter((entry) => entry.trim() !== '').length
                    } códigos carregados com sucesso.`,
                  });
                }
              })
            }
            isDisabled={isProcessing}
          >
            Importar CSV
          </Button>

          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isProcessing} size="sm">
              Cancelar
            </Button>
            <Button
              isDisabled={!canProcess}
              leftIcon={<FaPlus />}
              isLoading={isProcessing}
              variant="solid"
              onClick={processSubscriptions}
              colorScheme="green"
              size="sm"
              px={6}
            >
              {isProcessing ? 'Processando...' : 'Vincular'}
            </Button>
          </HStack>
        </Flex>
      </Box>
    </VStack>
  );
}
